<template>
    <div>
        <Hero
            :title="$t('site.title')"
            :lead="$t('homepage.hero.lead')"
            :primary-button="{ label: $t('generic.contact_us'), url: localePath('/job-seeker/contact') }"
            :secondary-button="{ label: $t('jobs.title'), url: localePath('/job-seeker/jobs') }"
            :illustration="require('~/assets/images/illustrations/b-mask_construction-worker.png')"
            :decoration="require('~/assets/images/illustrations/decorator-dots-yellow.svg')"
        />

        {{/* Popular Categories */}}
        <div>
            <div
                v-if="popularFilters && popularFilters.length"
                class="container pt-16 sm:pt-4 lg:pt-12 xl:pt-24 pb-16 md:pb-20 lg:pb-24 xl:pb-32"
            >
                <SectionWrapper
                    :title="$t('generic.popular_categories')"
                    :link="{ path: localePath('/job-seeker/jobs'), label: $t('generic.all_categories') }"
                >
                    <div>
                        <NuxtLink
                            v-for="(filter, i) in popularFilters"
                            :key="i"
                            :to="localePath(filter.path)"
                            class="
                                p-button p-button-secondary p-button-outlined p-button-light
                                m-2
                                py-2
                                px-3
                                sm:px-6 sm:py-4
                            "
                        >
                            <span class="p-button-label">{{ filter.label }}</span>
                        </NuxtLink>
                    </div>
                </SectionWrapper>
            </div>

            {{/* Newest Positions */}}
            <div v-if="newestJobs && newestJobs.length" class="relative">
                <div class="container px-0 relative z-10">
                    <SectionWrapper
                        :title="$t('generic.latest_offers')"
                        :link="{ path: localePath('/job-seeker/jobs'), label: $t('generic.all_offers') }"
                        is-carousel-wrapper
                    >
                        <JobsCarousel :jobs="newestJobs" />
                    </SectionWrapper>
                </div>

                <div class="absolute bottom-0 left-0 w-full h-60 sm:h-32 purple-gradient-bg rounded-tr-100 z-0"></div>
            </div>

            {{/* Advices for job-seekers */}}

            <div v-if="advices && advices.length" class="purple-gradient-bg pt-16 lg:pt-22">
                <div class="container px-0 relative">
                    <SectionWrapper
                        :title="$t('advice.title')"
                        :link="{ path: localePath('/job-seeker/advice'), label: $t('generic.more_tips') }"
                        is-carousel-wrapper
                    >
                        <ArticleCarousel :articles="advices" advice />
                    </SectionWrapper>
                </div>
            </div>
        </div>

        {{/* Why choose us */}}
        <div class="relative pt-36 sm:pt-40 lg:pt-52 pb-16 bg-white text-black">
            <div
                v-if="advices && advices.length"
                class="absolute top-0 left-0 w-full h-16 sm:h-22 lg:h-28 purple-gradient-bg rounded-bl-100 z-0"
            ></div>

            <div class="container">
                <SectionWrapper :title="$t('about_us.lead')">
                    <FeatureCard
                        :title="$t('job_seekers.choose_us_reasons.reason_1.title')"
                        :description="$t('job_seekers.choose_us_reasons.reason_1.description')"
                        :illustration="require('~/assets/images/illustrations/stock-01.jpg')"
                        rounded-t-r
                    />

                    <FeatureCard
                        :title="$t('job_seekers.choose_us_reasons.reason_2.title')"
                        :description="$t('job_seekers.choose_us_reasons.reason_2.description')"
                        :illustration="require('~/assets/images/illustrations/stock-02.jpg')"
                        reversed
                        rounded-t-r
                        rounded-b-l
                    />

                    <FeatureCard
                        :title="$t('job_seekers.choose_us_reasons.reason_3.title')"
                        :description="$t('job_seekers.choose_us_reasons.reason_3.description')"
                        :illustration="require('~/assets/images/illustrations/stock-03.jpg')"
                        rounded-b-r
                    />
                </SectionWrapper>
            </div>

            <div class="flex justify-center flex-wrap pt-4 px-2">
                <NuxtLink :to="localePath('/about-us')" class="p-button p-button-secondary p-button-outlined m-2">
                    <span class="p-button-label">{{ $t('about_us.title') }}</span>
                </NuxtLink>
            </div>
        </div>

        {{/* Partners */}}
        <div class="bg-gray-800 pt-20 pb-20">
            <SectionPartners :partners="partners" :title="$t('generic.partners')" class="container" />
        </div>

        {{/* Testimonials */}}
        <SectionTestimonials v-if="testimonials && testimonials.length" :testimonials="testimonials" />
    </div>
</template>

<script>
import Hero from '@/components/Heroes/Hero';
import SectionWrapper from '@/components/UI/SectionWrapper';
import FeatureCard from '@/components/Cards/FeatureCard/FeatureCard';
import SectionPartners from '@/components/Sections/SectionPartners/SectionPartners';
import SectionTestimonials from '@/components/Sections/SectionTestimonials/SectionTestimonials';
import JobsCarousel from '@/components/Carousels/JobsCarousel';
import ArticleCarousel from '@/components/Carousels/ArticleCarousel';

export default {
    name: 'Index',
    components: {
        Hero,
        SectionWrapper,
        FeatureCard,
        SectionPartners,
        SectionTestimonials,
        JobsCarousel,
        ArticleCarousel,
    },
    async asyncData({ $axios, redirect, localePath, $getIsLangKoZhDe }) {
        if ($getIsLangKoZhDe()) {
            return redirect(localePath('/'));
        }

        let partners = null;
        let testimonials = null;
        let newestJobs = null;
        let popularFilters = null;
        let advices = null;

        try {
            await Promise.all([
                $axios.$get('/partner'),
                $axios.$get('/testimonial?type=2'),
                $axios.$get('/job?limit=3'),
                $axios.$get('/job/popular-filters'),
                $axios.$get('/advice?limit=3'),
            ])
                .then(([partnersRes, testimonialRes, jobRes, popularFiltersRes, adviceRes]) => {
                    if (partnersRes.success === 1) {
                        partners = partnersRes.data;
                    }

                    if (testimonialRes.success === 1) {
                        testimonials = testimonialRes.data;
                    }

                    if (jobRes.success === 1) {
                        newestJobs = jobRes.data;
                    }

                    if (popularFiltersRes.success === 1) {
                        popularFilters = Object.keys(popularFiltersRes.data).reduce((acc, key) => {
                            popularFiltersRes.data[key].forEach((filter) => {
                                acc.push({
                                    label: filter.name ? filter.name : filter.title,
                                    path: `/job-seeker/jobs?${key}=${filter.id}`,
                                });
                            });

                            return acc;
                        }, []);
                    }

                    if (adviceRes.success === 1) {
                        advices = adviceRes.data;
                    }
                })
                .catch((err) => {
                    console.log(err);
                });
        } catch (err) {
            console.log(err);
        }

        return { partners, testimonials, newestJobs, popularFilters, advices };
    },
};
</script>

<style scoped></style>
